import moment from 'moment';
import { Observable, of } from 'rxjs';

import { OVERTIME_AUTOMATION_OVERRIDE_CODE } from '@shared-constants/cat-codes-constants';
import { DATE_FORMAT_TOR } from '@shared-constants/date-format-constants';
import { PayrollTypeFullEnum, TimesheetStatusEnum } from '@shared-enums/time.enum';
import { getDayString } from '@shared-helpers/date-helpers';
import { addDecimals } from '@shared-helpers/string-number-helpers';
import * as sharedApi from '@shared-interfaces/api';
import { IDayOfWeek } from '@shared-interfaces/dates';

import { ICatRecord } from '../interfaces/incoming';
import { ITotal, IWeeklyTotal } from '../interfaces/internal-interfaces';
import { Employee } from './employee';
import { NonbillableCatsRecord } from './NonbillableCatsRecord';
import { PayrollTimesheet } from './PayrollTimesheet';

export class PayrollSimulator {
  public payrollSimulation = this.buildPayrollSimulator([]);
  public isInSync = false;
  public isPayrollAutomationOverridden = false;

  private records: sharedApi.IPayrollTimeEvalResultSetResult[] = [];
  private payrollAutomationOverrideRecord?: ICatRecord;
  private readonly ignoreCodes = ['8001', '8010', '8056', '9153', '9154', OVERTIME_AUTOMATION_OVERRIDE_CODE];

  handleTimeEvalSet(
    timeEvalEmployee: sharedApi.IPayrollTimeEvalResult,
    payrollTotals: IWeeklyTotal,
    overrideRecord?: ICatRecord,
  ): void {
    const results = timeEvalEmployee.TimeEvalResultSet.results ?? [];
    if (results.length === 0 && this.findTotalOfTotals(payrollTotals) !== '0') {
      this.isInSync = false;
      return;
    }
    if (overrideRecord) this.handleOvertimeAutomationCase(overrideRecord);
    else this.isPayrollAutomationOverridden = false;
    this.records = timeEvalEmployee.TimeEvalResultSet.results.filter((x) => !this.ignoreCodes.includes(x.Code)); //we don't want pto balance to factor
    this.payrollSimulation = this.buildPayrollSimulator(this.records);
    this.isInSync = this.compareActualAndSimulation(this.payrollSimulation, payrollTotals);
  }

  private findTotalOfTotals(weeklyTotal: IWeeklyTotal): string {
    let total = '0';
    Object.keys(weeklyTotal).forEach((otType: string) => {
      total = addDecimals(total, weeklyTotal[otType].total);
    });
    return total;
  }

  private compareActualAndSimulation(payrollSimulation: IWeeklyTotal, actualTotals: IWeeklyTotal): boolean {
    let isEqual = true;
    (Object.keys(payrollSimulation) as Array<keyof IWeeklyTotal>).forEach((otType) => {
      if (payrollSimulation[otType].total !== actualTotals[otType].total) isEqual = false;
    });
    return isEqual;
  }

  private buildPayrollSimulator(records: sharedApi.IPayrollTimeEvalResultSetResult[]): IWeeklyTotal {
    const simulationObj = this.makeEmptySimulationObject();
    records.forEach((record) => {
      const workDateAsMoment = moment(record.Date, DATE_FORMAT_TOR);
      const day = getDayString(workDateAsMoment);
      const type = this.determineTimeType(record.Code);
      simulationObj[type][day] = addDecimals(simulationObj[type][day], record.Hrs);
      simulationObj[type].total = addDecimals(simulationObj[type].total, record.Hrs);
    });
    return simulationObj;
  }

  private handleOvertimeAutomationCase(record: ICatRecord): void {
    this.isPayrollAutomationOverridden = true;
    this.payrollAutomationOverrideRecord = record;
  }

  togglePayrollOverride$(
    shouldOverride: boolean,
    employee: Employee,
    payrollTimesheet: PayrollTimesheet,
  ): Observable<ICatRecord[] | null> {
    let record: NonbillableCatsRecord = new NonbillableCatsRecord(employee.datahub.nonBillable);
    if (shouldOverride && !this.isPayrollAutomationOverridden) {
      const date = this.findFirstValidDate(employee.InvalidDates);
      record = record.getRequiredObject(employee, '', payrollTimesheet.getDate(date));
      record.Extdocumentno = payrollTimesheet.nonbillableExtDocument.Documentno;
      record.Catshours = '00.01';
      record.AbsAttType = OVERTIME_AUTOMATION_OVERRIDE_CODE;
    } else if (!shouldOverride && this.isPayrollAutomationOverridden) {
      record = record.fillStateFromSap(this.payrollAutomationOverrideRecord);
      record.__shouldDelete = true;
    } else return of(null);
    payrollTimesheet.nonbillableExtDocument.updateSelf(TimesheetStatusEnum.InProgress);
    payrollTimesheet.determineTimesheetStatus();
    const categories = employee.datahub.catsDB.filterRecordsByOperation([record]);
    return employee.save$(categories);
  }

  private findFirstValidDate(invalidDates: string): IDayOfWeek {
    if (!invalidDates.includes('0')) return 'sun';
    if (!invalidDates.includes('1')) return 'mon';
    if (!invalidDates.includes('2')) return 'tue';
    if (!invalidDates.includes('3')) return 'wed';
    if (!invalidDates.includes('4')) return 'thu';
    if (!invalidDates.includes('5')) return 'fri';
    if (!invalidDates.includes('6')) return 'sat';
    return 'sun'; //should never happen, but we will default to the first day of the week in this case
  }

  private determineTimeType(code: string): PayrollTypeFullEnum {
    if (code === '7701') return PayrollTypeFullEnum.Overtime;
    if (code === '7702') return PayrollTypeFullEnum.Double;
    // if(code == '7776') return PayrollTypeEnum.Holiday
    return PayrollTypeFullEnum.Straight;
  }

  private makeEmptySimulationObject(): IWeeklyTotal {
    return { s: this.makeSingleDay(), o: this.makeSingleDay(), d: this.makeSingleDay() };
    // h: this.makeSingleDay(),
  }
  private makeSingleDay(): ITotal {
    return { total: '0', sun: '0', mon: '0', tue: '0', wed: '0', thu: '0', fri: '0', sat: '0' };
  }
}
